import { Injectable } from '@angular/core';
import { ApplicantOcrDocumentModel } from 'services/models/hiring/applicant-ocr-document.model';

@Injectable()
export class OcrFieldStyleService {
  private lastOcrDataRef: ApplicantOcrDocumentModel[] | null = null;
  private lastUsesOcr = false;
  private ocrFilledFieldKeys = new Set<string>();
  private ocrFieldSourceByKey = new Map<string, string>();
  private ocrFieldValueMatchesByKey = new Map<string, boolean>();
  private initialFieldValueByKey = new Map<string, any>();

  private popoverCloseCurrent: (() => void) | null = null;

  private ensureCache(ocrData: ApplicantOcrDocumentModel[] | null, usesOcr: boolean): void {
    if (!usesOcr) {
      if (this.lastUsesOcr) {
        this.ocrFilledFieldKeys.clear();
        this.ocrFieldSourceByKey.clear();
        this.ocrFieldValueMatchesByKey.clear();
        this.lastOcrDataRef = null;
        this.lastUsesOcr = false;
      }
      return;
    }
    if (ocrData === this.lastOcrDataRef && this.lastUsesOcr) {
      return;
    }
    this.lastOcrDataRef = ocrData ?? null;
    this.lastUsesOcr = usesOcr;
    this.ocrFilledFieldKeys.clear();
    this.ocrFieldSourceByKey.clear();
    this.ocrFieldValueMatchesByKey.clear();
    if (!ocrData?.length) {
      return;
    }
    for (const doc of ocrData) {
      const prefix = doc.applicantDependentId
        ? `dependent:${doc.applicantDependentId}:`
        : 'applicant:';
      for (const f of doc.fields || []) {
        const name = (f.columnName || '').toLowerCase();
        if (name) {
          this.ocrFilledFieldKeys.add(prefix + name);
          const source = (f.documentTypeTitle || f.label || 'Documento').trim() || 'Documento';
          this.ocrFieldSourceByKey.set(prefix + name, source);
          this.ocrFieldValueMatchesByKey.set(prefix + name, f.valueMatchesEntity === true);
        }
      }
    }
  }

  /**
   * Atualiza o cache com os dados atuais de OCR. Deve ser chamado antes dos getters
   * quando ocrData ou usesOcr podem ter mudado. O cache só é reconstruído se a
   * referência de ocrData ou o valor de usesOcr tiverem mudado.
   */
  updateCache(ocrData: ApplicantOcrDocumentModel[] | null, usesOcr: boolean): void {
    this.ensureCache(ocrData, usesOcr);
  }

  /**
   * Retorna texto para exibir no popover: origem do valor (documento de onde foi extraído).
   */
  getOcrFieldSourceText(
    ocrData: ApplicantOcrDocumentModel[] | null,
    usesOcr: boolean,
    fieldName: string,
    isApplicantField: boolean = true,
    dependentId?: string | null
  ): string {
    this.ensureCache(ocrData, usesOcr);
    const key = isApplicantField
      ? 'applicant:' + (fieldName || '').toLowerCase()
      : `dependent:${dependentId ?? ''}:` + (fieldName || '').toLowerCase();
    return this.ocrFieldSourceByKey.get(key);
  }

  /**
   * Verifica se o campo foi preenchido por OCR (candidato ou dependente).
   */
  isFieldFilledByOcr(
    ocrData: ApplicantOcrDocumentModel[] | null,
    usesOcr: boolean,
    fieldName: string,
    isApplicantField: boolean = true,
    dependentId?: string | null
  ): boolean {
    this.ensureCache(ocrData, usesOcr);
    const key = isApplicantField
      ? 'applicant:' + (fieldName || '').toLowerCase()
      : `dependent:${dependentId ?? ''}:` + (fieldName || '').toLowerCase();
    return this.ocrFilledFieldKeys.has(key);
  }

  /**
   * Estado único para cor e ícone: mesma lógica para ambos, evitando dessincronia.
   * Exibir cor/ícone apenas quando valueMatchesEntity for true E o usuário
   * ainda não alterou o valor em relação ao snapshot inicial.
   */
  getOcrFieldDisplayState(
    ocrData: ApplicantOcrDocumentModel[] | null,
    usesOcr: boolean,
    fieldName: string,
    isApplicantField: boolean,
    dependentId: string | null | undefined,
    currentValue: any
  ): { fieldOcrFilled: boolean; showIcon: boolean } {
    this.ensureCache(ocrData, usesOcr);
    const key = isApplicantField
      ? 'applicant:' + (fieldName || '').toLowerCase()
      : `dependent:${dependentId ?? ''}:` + (fieldName || '').toLowerCase();
    const filledByOcr = this.ocrFilledFieldKeys.has(key);
    const valueMatchesEntity = this.ocrFieldValueMatchesByKey.get(key) === true;
    const userChanged = this.hasUserChangedValue(key, currentValue);
    const show = filledByOcr && valueMatchesEntity && !userChanged;
    return { fieldOcrFilled: show, showIcon: show };
  }

  /**
   * Retorna objeto para ngClass indicando origem OCR do valor.
   * Aplica a classe de cor apenas quando valueMatchesEntity for true E o usuário
   * ainda não alterou o valor em relação ao snapshot inicial.
   */
  getOcrFieldClasses(
    ocrData: ApplicantOcrDocumentModel[] | null,
    usesOcr: boolean,
    fieldName: string,
    isApplicantField: boolean = true,
    dependentId?: string | null,
    currentValue?: any
  ): { [key: string]: boolean } {
    const state = this.getOcrFieldDisplayState(
      ocrData,
      usesOcr,
      fieldName,
      isApplicantField,
      dependentId,
      currentValue
    );
    return { 'field-ocr-filled': state.fieldOcrFilled };
  }

  /**
   * Define o valor inicial de um campo (snapshot) para comparação futura.
   * Deve ser chamado pelo componente quando o formulário atinge o "estado base"
   * após OCR / preenchimento automático.
   */
  setInitialFieldValue(
    fieldName: string,
    initialValue: any,
    isApplicantField: boolean = true,
    dependentId?: string | null
  ): void {
    const key = isApplicantField
      ? 'applicant:' + (fieldName || '').toLowerCase()
      : `dependent:${dependentId ?? ''}:` + (fieldName || '').toLowerCase();
    this.initialFieldValueByKey.set(key, initialValue);
  }

  /**
   * Lógica central para saber se o usuário alterou o valor em relação ao snapshot.
   */
  private hasUserChangedValue(key: string, currentValue: any): boolean {
    if (!this.initialFieldValueByKey.has(key)) {
      return false;
    }
    const initial = this.initialFieldValueByKey.get(key);
    // Aqui a comparação é simples; se precisar, podemos normalizar (trim, upper, etc.)
    return initial !== currentValue;
  }

  /**
   * Indica se ainda devemos exibir o ícone de OCR (popover). Usa a mesma
   * lógica que a cor (getOcrFieldDisplayState): some quando o usuário alterar o valor.
   */
  shouldShowOcrIcon(
    ocrData: ApplicantOcrDocumentModel[] | null,
    usesOcr: boolean,
    fieldName: string,
    currentValue: any,
    isApplicantField: boolean = true,
    dependentId?: string | null
  ): boolean {
    const state = this.getOcrFieldDisplayState(
      ocrData,
      usesOcr,
      fieldName,
      isApplicantField,
      dependentId,
      currentValue
    );
    return state.showIcon;
  }

  /** Registra o popover que acabou de abrir; fecha o anterior se existir. */
  registerPopoverOpen(closeFn: () => void): void {
    if (this.popoverCloseCurrent) {
      this.popoverCloseCurrent();
    }
    this.popoverCloseCurrent = closeFn;
  }

  /** Remove o popover do registro ao fechar. */
  unregisterPopover(closeFn: () => void): void {
    if (this.popoverCloseCurrent === closeFn) {
      this.popoverCloseCurrent = null;
    }
  }
}
